import {
	createContext,
	forwardRef,
	Profiler,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import {
	Button,
	ButtonGroup,
	Collapse,
	Dropdown,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	PopoverBody,
	Tooltip,
	UncontrolledDropdown,
	UncontrolledPopover,
} from "reactstrap";
import moment from "moment";
import { TableVirtuoso } from "react-virtuoso";

import "../../styles/DataValidation.css";
import "../../styles/StackPlots.css";
import "../../styles/ValidationFilterModal.css";
import { default as utilIcons } from "../../resources/utilIcons";
import { colors } from "../../styles/colors";

import ValidationPlot from "./ValidationPlot";
import { ModalContext, RestContext } from "../../App";
import { DATA_URL, STACKPLOT_URL, VALIDATION_URL } from "../../serverConfig";
import {
	checkMobile,
	compArray,
	exists,
	ifExists,
	includesArray,
} from "../../util/globals";
import SortControls from "../../components/SortControls";
import { deTzTimestamp } from "../../util/timeUtils";
import clamp from "../../util/clamp";
import HeaderSortButtons from "../../components/HeaderSortButtons";
import { rotate } from "ol/transform";
import { data } from "./StackPlotDummy";

export const DataValidationContext = createContext(null);

const COLUMN_LIST = [
	"recordDate",
	"parCode",
	"rawVal",
	"screenFlag",
	"loggerFlag",
	"qualifierCode",
	"validationCode",
	"controlVal",
	"validatedVal",
	"sourceCode",
	"status",
	"insertDate",
	"updateDate",
	"updateBy",
];

const AQMET_COLUMNS = {
	recordDate: "Record Date",
	parCode: "Par Code",
	rawVal: "Raw Val",
	screenFlag: "Screening Flag",
	loggerFlag: "Logger Flag",
	qualifierCode: "Qualifier Code(s)",
	validationCode: "Validation Code",
	controlVal: "Control Val",
	validatedVal: "Validated Val",
	sourceCode: "Source Code",
	status: "Status",
	insertDate: "Insert Date",
	updateDate: "Update Date",
	updateBy: "Update By",
};

const EDITABLE_COLUMNS = {
	recordDate: false,
	parCode: false,
	rawVal: true,
	screenFlag: false,
	loggerFlag: false,
	qualifierCode: true,
	validationCode: false,
	controlVal: false,
	validatedVal: false,
	sourceCode: true,
	status: true,
	insertDate: false,
	updateDate: false,
	updateBy: false,
};

const COLUMN_INDECES = {
	recordDate: 1,
	parCode: 2,
	rawVal: 3,
	screenFlag: 4,
	loggerFlag: 5,
	qualifierCode: 6,
	validationCode: 7,
	controlVal: 8,
	validatedVal: 9,
	sourceCode: 10,
	status: 11,
	insertDate: 12,
	updateDate: 13,
	updateBy: 14,
};

const COLUMN_TYPES = {
	recordDate: "datetime-local",
	parCode: "text",
	rawVal: "number",
	screenFlag: "text",
	loggerFlag: "text",
	qualifierCode: "text",
	validationCode: "text",
	controlVal: "text",
	validatedVal: "number",
	sourceCode: "text",
	status: "text",
	insertDate: "datetime-local",
	updateDate: "datetime-local",
	updateBy: "text",
};

const CAL_COLUMN_LIST = [
	"recordDate",
	"parCode",
	"calType",
	"valType",
	"calVal",
	"flags",
	"validationCode",
	"valCalVal",
	"controlVal",
	"aqsAgency",
	"comment",
];

const CAL_AQMET_COLUMNS = {
	recordDate: "Record Date",
	parCode: "Par Code",
	calType: "Cal Type",
	valType: "Val Type",
	calVal: "Cal Val",
	flags: "Flags",
	validationCode: "Validation Code",
	valCalVal: "Val Cal Val",
	controlVal: "Control Val",
	aqsAgency: "AQS Agency",
	comment: "Comment",
};

const CAL_EDITABLE_COLUMNS = {
	recordDate: false,
	parCode: false,
	calType: false,
	valType: false,
	calVal: true,
	flags: false,
	validationCode: false,
	valCalVal: false,
	controlVal: false,
	aqsAgency: true,
	comment: true,
};

const CAL_COLUMN_INDECES = {
	recordDate: 1,
	parCode: 2,
	calType: 3,
	valType: 4,
	calVal: 5,
	flags: 6,
	validationCode: 7,
	valCalVal: 8,
	controlVal: 9,
	aqsAgency: 10,
	comment: 11,
};

const CAL_COLUMN_TYPES = {
	recordDate: "datetime-local",
	parCode: "text",
	calType: "text",
	valType: "text",
	calVal: "number",
	flags: "text",
	validationCode: "text",
	valCalVal: "number",
	controlVal: "text",
	aqsAgency: "text",
	comment: "text",
};

const CAL_TYPES = [
	"Span",
	"Prec",
	"Zero",
	"GSpan",
	"GZero",
	"Diag-Prec",
	"Mflow-Prec",
	"Tflow-Prec",
];

function DataValidation({ selectedSite }) {
	const [dateRange, setDateRange] = useState({
		min: moment()
			.set("hour", 0)
			.set("minute", 0)
			.set("second", 0)
			.set("millisecond", 0)
			.set("date", 1)
			.subtract(1, "month")
			.format("YYYY-MM-DDTHH:mm"),
		max: moment()
			.set("hour", 0)
			.set("minute", 0)
			.set("second", 0)
			.set("millisecond", 0)
			.set("date", 1)
			.format("YYYY-MM-DDTHH:mm"),
	});
	const [dataType, setDataType] = useState("primary");
	const [calTypes, setCalTypes] = useState([]);
	const [parCodeList, setParCodeList] = useState([]);
	const [parCodeRoundingMap, setParCodeRoundingMap] = useState(new Map());
	const [parCodeIntervalMap, setParCodeIntervalMap] = useState(new Map());
	const [selectedPars, setSelectedPars] = useState([]);
	const [columns, setColumns] = useState([]);
	const [data, setData] = useState([]);
	const [stackplotData, setStackplotData] = useState({});
	const [loading, setLoading] = useState(false);
	const [loadingProgress, setLoadingProgress] = useState(1);
	const [chartRefs, setChartRefs] = useState();
	const [callbacks, setCallbacks] = useState({});
	const [dataChanged, setDataChanged] = useState(false);
	const [dataChanges, setDataChanges] = useState({});
	const [optionsChanged, setOptionsChanged] = useState(false);
	const [highlighted, setHighlighted] = useState({});
	const [sortedData, setSortedData] = useState(null);
	const [stackPlotConfig, setStackPlotConfig] = useState(null);
	const [errorCode, setErrorCode] = useState(null); // Oracle error code returned by bad validation request
	const [sortColumns, setSortColumns] = useState([
		["recordDate", "Asc"],
		["parCode", "Asc"],
	]);
	const [autoYAxis, setAutoYAxis] = useState(true);
	const [fixedYScales, setFixedYScales] = useState({});
	const [filterFns, setFilterFns] = useState([]);
	const [filterString, setFilterString] = useState("");
	const [plotPopout, setPlotPopout] = useState(false);
	const [popoutWindow, setPopoutWindow] = useState(null);
	const [zoomLevel, setZoomLevel] = useState(100);

	const { sendGetRequest, sendPostRequest } = useContext(RestContext);
	const { openModal, setOpenModal } = useContext(ModalContext);

	const loadingOptions = useRef(loading);
	const stackPlotRef = useRef(null);
	const virtuosoRef = useRef(null);
	const dataRef = useRef([]);
	const dataToSortedMap = useRef({});
	const sortedToDataMap = useRef({});

	const toggleSortModal = () =>
		openModal === "validation-sort-modal"
			? setOpenModal()
			: setOpenModal("validation-sort-modal");
	const toggleFilterModal = () =>
		openModal === "validation-filter-modal"
			? setOpenModal()
			: setOpenModal("validation-filter-modal");
	const toggleInsertModal = () =>
		openModal === "validation-insert-modal"
			? setOpenModal()
			: setOpenModal("validation-insert-modal");
	const toggleAutoYAxis = () => setAutoYAxis(!autoYAxis);

	useEffect(() => console.log(stackPlotConfig), [stackPlotConfig]);

	useEffect(() => {
		sendGetRequest(VALIDATION_URL + "/scales", (response) =>
			setFixedYScales(response.data),
		);
	}, []);

	useEffect(() => {
		setOptionsChanged(selectedPars.length > 0); // Can load data whenever date range is updated or selected parameter list is updated (and not empty)
		resetDataChanges();
		setDataChanged(false);
		if (!!errorCode) {
			setErrorCode(null);
		}
	}, [dateRange, selectedPars, dataType, calTypes]);

	useEffect(() => {
		setSortedData(data);
		setSortColumns([
			["recordDate", "Asc"],
			["parCode", "Asc"],
		]);
		setFilterFns([]);
		setHighlighted({});
		const newIndexMap = Object.fromEntries(
			data.map((val, index) => [index, index]),
		);
		dataToSortedMap.current = newIndexMap;
		sortedToDataMap.current = newIndexMap;
		// convertDataForStackPlots(data);
		getConfig();
		dataRef.current = data;
	}, [data]);

	useEffect(() => {
		const sortFn = buildSortFunction(0);
		sortData(sortFn);
	}, [sortColumns, filterFns]);

	useEffect(() => {
		convertDataForStackPlots(sortedData);
	}, [sortedData]);

	useEffect(() => {
		console.log(highlighted);
	}, [highlighted]);

	const isHighlighted = (key, timestamp) => {
		if (Array.isArray(key)) {
			return highlighted[timestamp]?.some((arr) =>
				arr.every((val, index) => val === key[index]),
			);
		} else {
			return highlighted[timestamp]?.includes(key);
		}
	};

	const buildSortFunction = (index) => {
		if (index >= sortColumns.length) {
			return (a, b) => 0;
		}
		const column = sortColumns[index][0];
		const direction = sortColumns[index][1] === "Asc" ? 1 : -1;
		const nextColumnFn = buildSortFunction(index + 1);
		const sortFn = (a, b) => {
			if (!a || !b) return 0;
			else {
				if (a[column] === b[column]) {
					return nextColumnFn(a, b);
				} else {
					if (b[column] === null) return direction;
					else if (a[column] === null) return -direction;
					else return a[column] > b[column] ? direction : -direction;
				}
			}
		};
		return sortFn;
	};

	const sortData = (sortFn) => {
		if (!sortFn) return;
		let newSortedData = [...data];
		for (let fn of filterFns) {
			newSortedData = newSortedData.filter(fn);
		}
		newSortedData.sort(sortFn);
		const newDataToSortedMap = {};
		const newSortedToDataMap = {};
		for (let index in newSortedData) {
			const dataIndex = data.indexOf(newSortedData[index]);
			newDataToSortedMap[dataIndex] = index;
			newSortedToDataMap[index] = dataIndex;
		}
		dataToSortedMap.current = newDataToSortedMap;
		sortedToDataMap.current = newSortedToDataMap;
		setSortedData(newSortedData);
	};

	const convertDataForStackPlots = (sortedData) => {
		if (!data?.length || !sortedData) return;
		const timestamps = [];
		const minTimestamp = moment(data[0].recordDate).unix();
		const maxTimestamp = moment(data[data.length - 1].recordDate).unix();
		for (let i = minTimestamp; i <= maxTimestamp; i += 60) {
			timestamps.push(i);
		}
		const convertedData = { _timestamps: timestamps, _startTime: minTimestamp, _endTime: maxTimestamp };
		if (dataType === "cal") {
			for (let row of sortedData) {
				const rowKey = [row.parCode, row.calType, row.valType];
				if (!(rowKey in convertedData)) {
					convertedData[rowKey] = {};
				}
				const timestamp = moment(row.recordDate).unix();
				convertedData[rowKey][timestamp] = [
					row.calVal === -999 ? null : row.calVal,
					row.flags,
				];
				const valKey = [row.parCode + "_VAL", row.calType, row.valType];
				if (!(valKey in convertedData)) {
					convertedData[valKey] = {};
				}
				convertedData[valKey][timestamp] = [
					row.valCalVal === -999 ? null : row.valCalVal,
					row.validationCode,
				];
			}
		} else {
			for (let row of sortedData) {
				if (!(row.parCode in convertedData)) {
					convertedData[row.parCode] = {};
				}
				const timestamp = moment(row.recordDate).unix();
				convertedData[row.parCode][timestamp] = [
					row.rawVal === -999 ? null : row.rawVal,
					row.loggerFlag ? row.loggerFlag : row.screenFlag,
				];
				if (!(row.parCode + "_VAL" in convertedData)) {
					convertedData[row.parCode + "_VAL"] = {};
				}
				convertedData[row.parCode + "_VAL"][timestamp] = [
					row.validatedVal === -999 ? null : row.validatedVal,
					row.validationCode,
				];
			}
		}
		setStackplotData(convertedData);
	};

	const beginLoad = () => {
		setDataChanges({});
		const payload = {
			siteId: selectedSite,
			minDate: deTzTimestamp(dateRange.min),
			maxDate: deTzTimestamp(dateRange.max),
			parCodeList: selectedPars.map((par) => par.parCode),
			calTypes: dataType == "cal" ? calTypes : undefined,
		};
		setLoading(true);
		sendPostRequest(
			VALIDATION_URL + "/getData" + (dataType == "cal" ? "/cal" : ""),
			payload,
			(response) => {
				setData(response.data);
				setLoading(false);
				setOptionsChanged(false);
			},
			(error) => {
				setLoading(false);
			},
		);
	};

	const getMinDate = () => {
		if (!data?.length) return 0;
		return data[0].recordDate;
	};

	const getMaxDate = () => {
		if (!data?.length) return 0;
		return data[data.length - 1].recordDate;
	};

	const getMin = (data) => {
		if (data.length === 0) return -Infinity;
		else if (data.length === 1) return data[0];
		else {
			let min = data[0];
			for (let i = 1; i < data.length; i++) {
				if (data[i] < min) min = data[i];
			}
			return min;
		}
	};

	const getMax = (data) => {
		if (data.length === 0) return Infinity;
		else if (data.length === 1) return data[0];
		else {
			let max = data[0];
			for (let i = 1; i < data.length; i++) {
				if (data[i] > max) max = data[i];
			}
			return max;
		}
	};

	const doXScroll = (scrollOptions) => {
		if (
			!dateRange ||
			dateRange.min === undefined ||
			dateRange.max === undefined ||
			!stackplotData ||
			!stackplotData._timestamps ||
			stackplotData._timestamps.length < 2
		)
			return;
		const { direction, quantity, unit } = scrollOptions;
		let offset = 0;
		switch (direction) {
			case "left":
				let dataMin = moment.unix(stackplotData._timestamps[0]);
				let currentMin = moment(dateRange.min, "YYYY-MM-DDTHH:mm");
				let targetMin = moment(currentMin).subtract(quantity, unit);
				if (targetMin.unix() < dataMin.unix()) {
					offset = currentMin.unix() - dataMin.unix();
				} else {
					offset = currentMin.unix() - targetMin.unix();
				}
				setDateRange({
					min: currentMin
						.subtract(offset, "seconds")
						.format("YYYY-MM-DDTHH:mm"),
					max: moment(dateRange.max, "YYYY-MM-DDTHH:mm")
						.subtract(offset, "seconds")
						.format("YYYY-MM-DDTHH:mm"),
				});
				return;
			case "right":
				let dataMax = moment.unix(
					stackplotData._timestamps[
						stackplotData._timestamps.length - 1
					],
				);
				let currentMax = moment(dateRange.max, "YYYY-MM-DDTHH:mm");
				let targetMax = moment(currentMax).add(quantity, unit);
				if (targetMax.unix() > dataMax.unix()) {
					offset = dataMax.unix() - currentMax.unix();
				} else {
					offset = targetMax.unix() - currentMax.unix();
				}
				setDateRange({
					min: moment(dateRange.min, "YYYY-MM-DDTHH:mm")
						.add(offset, "seconds")
						.format("YYYY-MM-DDTHH:mm"),
					max: currentMax
						.add(offset, "seconds")
						.format("YYYY-MM-DDTHH:mm"),
				});
				return;
			default:
				return;
		}
	};

	const updateTimeframe = (minTimestamp, maxTimestamp) => {
		setDateRange({
			min: minTimestamp,
			max: maxTimestamp,
		});
	};

	const handleMinDate = (event) => {
		const value = event.target.value;
		console.log(value);
		setDateRange((values) => ({ ...values, min: value }));
	};

	const handleMaxDate = (event) => {
		const value = event.target.value;
		setDateRange((values) => ({ ...values, max: value }));
	};

	const getConfig = () => {
		console.log(selectedPars);
		sendGetRequest(
			`${STACKPLOT_URL}/validation/${selectedSite}?type=${dataType}`,
			(response) => {
				processConfig(response.data);
			},
			(error) => {
				setStackPlotConfig(generateConfig());
			},
		);
	};

	const processConfig = (config) => {
		if (dataType === "cal") {
			processCalConfig(config);
			return;
		}
		const newCharts = [];
		const usedPars = [];
		for (let chart of config.charts) {
			const newParameters = [];
			for (let parameter of chart.parameters) {
				if (
					selectedPars.some(
						(par) => par.parCode === parameter.parCode,
					)
				) {
					usedPars.push(parameter.parCode);
					newParameters.push(parameter);
				}
			}
			chart.parameters = newParameters;
			if (chart.parameters.length) {
				newCharts.push(chart);
			}
		}
		const unusedPars = selectedPars.filter(
			(par) => !usedPars.some((parCode) => par.parCode === parCode),
		);
		for (let par of unusedPars) {
			const parConfig = {
				parCode: par.parCode,
				unit: par.unitCode,
				type: "line",
				side: "L",
				color: "0000FF",
				symbol: null,
				rawPrec: par.rawPrec,
			};
			const chartConfig = {
				leftMin: null,
				leftMax: null,
				rightMin: null,
				rightMax: null,
				chartHeight: 60,
				hasRightSide: false,
				parameters: [parConfig],
			};
			newCharts.push(chartConfig);
		}

		config.charts = newCharts;
		setStackPlotConfig(config);
	};

	const processCalConfig = (config) => {
		const newCharts = [];
		const usedPars = [];
		for (let chart of config.charts) {
			const newParameters = [];
			for (let parameter of chart.parameters) {
				if (
					selectedPars.some(
						(par) => par.parCode === parameter.parCode,
					)
				) {
					if (
						calTypes.some(
							(calType) => parameter.calType === calType,
						)
					) {
						usedPars.push([
							parameter.parCode,
							parameter.calType,
							parameter.valType,
						]);
						newParameters.push(parameter);
					}
				}
			}
			chart.parameters = newParameters;
			if (chart.parameters.length) {
				newCharts.push(chart);
			}
		}

		const unusedCalPars = {};
		for (const par of selectedPars) {
			unusedCalPars[par.parCode] = calTypes.filter(
				(type) =>
					!usedPars.some(
						([parCode, calType, valType]) =>
							type === calType && par.parCode === parCode,
					),
			);
		}
		console.log(unusedCalPars);
		for (let par of selectedPars) {
			if (par.parameter === "O3CAL" || !unusedCalPars[par.parCode].length)
				continue;
			const indicatedPar = par;
			const actualPar =
				par.parameter === "O3"
					? selectedPars.find(
							(found) => found.parameter === "O3CAL",
						) || par
					: par;
			for (let calType of unusedCalPars[par.parCode]) {
				const parConfigActual = {
					parCode: actualPar.parCode,
					calType: calType,
					valType: "ACTUAL",
					unit: actualPar.unitCode,
					type: "line",
					side: "L",
					color: "0000FF",
					symbol: null,
					rawPrec: actualPar.rawPrec,
				};
				const parConfigIndicated = {
					parCode: indicatedPar.parCode,
					calType: calType,
					valType: "INDICATED",
					unit: indicatedPar.unitCode,
					type: "line",
					side: "L",
					color: "FF0000",
					symbol: null,
					rawPrec: indicatedPar.rawPrec,
				};
				const chartConfig = {
					leftMin: null,
					leftMax: null,
					rightMin: null,
					rightMax: null,
					chartHeight: 60,
					hasRightSide: false,
					parameters: [parConfigActual, parConfigIndicated],
				};
				newCharts.push(chartConfig);
			}
		}
		config.charts = newCharts;
		setStackPlotConfig(config);
	};

	const generateConfig = () => {
		const newConfig = {
			fullTitle: null,
			footnote: null,
			chartSpacing: 20,
			charts: [],
			dataType: dataType === "cal" ? "VALCAL" : "VAL",
		};
		if (!selectedPars?.length) {
			return newConfig;
		} else {
			if (dataType === "cal") {
				for (let par of selectedPars) {
					if (par.parameter === "O3CAL") continue;
					const indicatedPar = par;
					const actualPar =
						par.parameter === "O3"
							? selectedPars.find(
									(found) => found.parameter === "O3CAL",
								) || par
							: par;
					for (let calType of calTypes) {
						const parConfigActual = {
							parCode: actualPar.parCode,
							calType: calType,
							valType: "ACTUAL",
							unit: actualPar.unitCode,
							type: "line",
							side: "L",
							color: "FF0000",
							symbol: null,
							rawPrec: actualPar.rawPrec,
						};
						const parConfigIndicated = {
							parCode: indicatedPar.parCode,
							calType: calType,
							valType: "INDICATED",
							unit: indicatedPar.unitCode,
							type: "line",
							side: "L",
							color: "0000FF",
							symbol: null,
							rawPrec: indicatedPar.rawPrec,
						};
						const parDiff = {
							parCode: `${indicatedPar.parCode} ${calType} ${calType === "Zero" || calType === "GZero" ? "DIFF" : "PDIFF"}`,
							calType: calType,
							valType:
								calType === "Zero" || calType === "GZero"
									? "DIFF"
									: "PDIFF",
							unit: "%",
							side: "R",
							color: "000000",
							symbol: null,
							rawPrec: 2,
						};
						const chartConfig = {
							leftMin: null,
							leftMax: null,
							rightMin: null,
							rightMax: null,
							chartHeight: 60,
							hasRightSide: true,
							parameters: [
								parConfigActual,
								parConfigIndicated,
								parDiff,
							],
						};
						newConfig.charts.push(chartConfig);
					}
				}
			} else {
				for (let par of selectedPars) {
					const parConfig = {
						parCode: par.parCode,
						unit: par.unitCode,
						type: "line",
						side: "L",
						color: "0000FF",
						symbol: null,
						rawPrec: par.rawPrec,
					};
					const chartConfig = {
						leftMin: null,
						leftMax: null,
						rightMin: null,
						rightMax: null,
						chartHeight: 60,
						hasRightSide: false,
						parameters: [parConfig],
					};
					newConfig.charts.push(chartConfig);
				}
			}
		}
		return newConfig;
	};

	const registerCallback = (callbackName, callbackFn) => {
		setCallbacks((values) => ({ ...values, [callbackName]: callbackFn }));
	};

	const scrollTableOnCursor = (timestamp, parCode) => {
		console.log(timestamp, parCode);
		if (!virtuosoRef.current) return;
		const ts = moment(timestamp).utc().format("YYYY-MM-DDTHH:mm:ss.SSSZ");
		const data = dataRef.current;
		let l = 0,
			r = data.length,
			m = Math.trunc((l + r) / 2);
		let row;
		while (l < m) {
			row = data[m];
			if (row.recordDate === ts) {
				break;
			} else if (row.recordDate < ts) {
				l = m;
			} else {
				r = m;
			}
			m = Math.trunc((l + r) / 2);
		}
		if (!(row.parCode === parCode && row.recordDate === ts)) {
			l = m - 1;
			r = m + 1;
			let lRow = data[l],
				rRow = data[r];
			while (lRow?.recordDate === ts || rRow?.recordDate === ts) {
				if (lRow?.recordDate === ts && lRow?.parCode === parCode) {
					m = l;
					break;
				} else if (
					rRow?.recordDate === ts &&
					rRow?.parCode === parCode
				) {
					m = r;
					break;
				}
				l--;
				r++;
				lRow = data[l];
				rRow = data[r];
			}
		}
		const tableIndex = dataToSortedMap.current[m];

		virtuosoRef.current.scrollIntoView({
			index: tableIndex,
			align: "center",
		});
	};

	const onClearCursors = () => {
		setHighlighted({});
	};

	const resetDataChanges = () => {
		//resetTableChanges();
		setDataChanges({});
		setDataChanged(false);
	};

	const getRowKey = (row) => {
		return row.calType
			? [row.parCode, row.calType, row.valType]
			: row.parCode;
	};

	const buildValidatedValFn = (valCode, controlVal) => {
		if (valCode === "V" || valCode === "VT") {
			return (rawVal) => rawVal;
		} else if (valCode === "VZ") {
			return (rawVal) => 0;
		} else if (valCode === "VA") {
			if (!controlVal?.length) {
				return (rawVal) => rawVal;
			} else if (!isNaN(controlVal[0])) {
				const adjVal = Number(controlVal);
				return (rawVal) => adjVal;
			} else {
				const adjVal = Number(controlVal.slice(1));
				switch (controlVal[0]) {
					case "-":
						return (rawVal) => rawVal - adjVal;
					case "+":
						return (rawVal) => rawVal + adjVal;
					case "/":
						return (rawVal) => rawVal / adjVal;
					case "*":
						return (rawVal) => rawVal * adjVal;
					case "=":
						return (rawVal) => adjVal;
					default:
						return (rawVal) => rawVal;
				}
			}
		} else if (valCode === "VM") {
			if (isNaN(controlVal)) {
				return (rawVal) => -999;
			} else {
				return (rawVal) => Math.min(Number(controlVal), rawVal);
			}
		} else {
			return (rawVal) => -999;
		}
	};

	const applyValidationInfo = (payload) => {
		if (!highlighted) return;
		const { selectedPars, validationCode, qualifierCode, controlVal } =
			payload;
		const highlightedTimestamps = Object.keys(highlighted);
		const minTimestamp = getMin(highlightedTimestamps),
			maxTimestamp = getMax(highlightedTimestamps);

		const validatedValFn = buildValidatedValFn(validationCode, controlVal);
		const rawColumn = dataType === "cal" ? "calVal" : "rawVal";
		const valColumn = dataType === "cal" ? "valCalVal" : "validatedVal";
		const commentColumn = dataType === "cal" ? "comment" : "qualifierCode";

		const newDataChanges = { ...dataChanges };
		for (let index in data) {
			const row = data[index];
			if (row.recordDate < minTimestamp) {
				continue;
			} else if (row.recordDate > maxTimestamp) {
				break;
			} else {
				if (
					isHighlighted(getRowKey(row), row.recordDate) &&
					selectedPars[row.parCode]
				) {
					const newRow = dataChanges[index]
						? { ...dataChanges[index] }
						: { ...row };
					newRow.validationCode = validationCode;
					newRow[commentColumn] = qualifierCode;
					newRow.controlVal =
						validationCode === "VA" || validationCode === "VM"
							? controlVal
							: null;
					newRow[valColumn] = validatedValFn(newRow[rawColumn]);
					newDataChanges[index] = newRow;
				}
			}
		}
		// applyDataChangesToTable(); // TODO remove?
		setDataChanges(newDataChanges);

		setDataChanged(true);
	};

	const applyBatchChange = (name, value) => {
		if (!highlighted) return;
		const highlightedTimestamps = Object.keys(highlighted);
		const minTimestamp = getMin(highlightedTimestamps),
			maxTimestamp = getMax(highlightedTimestamps);

		const newDataChanges = { ...dataChanges };
		for (let index in data) {
			const row = data[index];
			if (row.recordDate < minTimestamp) {
				continue;
			} else if (row.recordDate > maxTimestamp) {
				break;
			} else {
				if (isHighlighted(getRowKey(row), row.recordDate)) {
					const newRow = dataChanges[index]
						? { ...dataChanges[index] }
						: { ...row };
					newRow[name] = value;
					newDataChanges[index] = newRow;
				}
			}
		}
		// applyDataChangesToTable();
		setDataChanges(newDataChanges);

		setDataChanged(true);
	};

	const saveChanges = () => {
		if (!dataChanged) return;

		if (!!errorCode) {
			setErrorCode(null);
		}

		const payload = {
			siteId: selectedSite,
			data: [],
			oldData: [],
		};
		Object.entries(dataChanges).forEach(([key, value]) => {
			payload.data.push(value);
			payload.oldData.push(data[key]);
		});
		sendPostRequest(
			VALIDATION_URL + "/validate" + (dataType === "cal" ? "/cal" : ""),
			payload,
			(response) => {
				const newData = [...data];
				if (response.data?.length) {
					const dataChangesEntries = Object.entries(dataChanges);
					for (let newRow of response.data) {
						for (let [key, value] of dataChangesEntries) {
							if (
								newRow.parCode === value.parCode &&
								newRow.calType === value.calType &&
								newRow.valType === value.valType &&
								newRow.recordDate === value.recordDate
							) {
								newData[key] = newRow;
								break;
							}
						}
					}
				}

				// Object.entries(dataChanges).forEach(([value, key]) => {
				// 	newData[key] = value;
				// });
				setDataChanges({});
				setData(newData);
			},
			(error) => {
				const code = error.response?.data?.errorCode || null;
				setErrorCode(code);
			},
		);
	};

	const autoValidate = () => {
		const payload = {
			siteId: selectedSite,
			calTypes: calTypes,
			startDate: moment(dateRange.min).toISOString(),
			endDate: moment(dateRange.max).toISOString(),
			parCodes: selectedPars.map((par) => par.parCode),
		};
		sendPostRequest(
			VALIDATION_URL + "/auto" + (dataType === "cal" ? "/cal" : ""),
			payload,
			(response) => {
				beginLoad();
			},
		);
	};

	const autoValidateAll = () => {
		const payload = {
			siteId: selectedSite,
			calTypes: calTypes,
			startDate: moment(dateRange.min).toISOString(),
			endDate: moment(dateRange.max).toISOString(),
			parCodes: [],
		};
		sendPostRequest(
			VALIDATION_URL + "/auto" + (dataType === "cal" ? "/cal" : ""),
			payload,
			(response) => {
				beginLoad();
			},
		);
	};

	const handleHeaderSort = (columnName, direction) => {
		setSortColumns([[columnName, direction]]);
	};

	const insertRows = (rows) => {
		for (let row of rows) {
			row.recordDate = moment(row.recordDate)
				.utcOffset(0, false)
				.toISOString();
		}
		const payload = {
			siteId: selectedSite,
			data: rows,
		};
		sendPostRequest(
			VALIDATION_URL + "/insert" + (dataType === "cal" ? "/cal" : ""),
			payload,
			(response) => {
				setOpenModal();
				beginLoad();
			},
		);
	};

	const blankFill = (valCode) => {
		const payload = {
			siteId: selectedSite,
			validationCode: valCode,
			startTime: moment(dateRange.min).toISOString(),
			endTime: moment(dateRange.max).toISOString(),
			parCodes: selectedPars.map((par) => par.parCode),
		};
		sendPostRequest(VALIDATION_URL + "/fill", payload, (response) => {
			beginLoad();
		});
	};

	const getPlot = () => {
		return popoutWindow ? null : (
			<>
				<div className="stackplot-y-axis-toggle-wrapper">
					<PlotZoomControls
						zoomLevel={zoomLevel}
						setZoomLevel={setZoomLevel}
					/>
					<label>Auto Y-Axis</label>
					<input
						type="checkbox"
						checked={autoYAxis}
						onClick={toggleAutoYAxis}
						readOnly
					/>
					<Button onClick={handlePopout}>Popout</Button>
				</div>
				<div id="stackplot-controls-help">
					<div className="stackplot-controls-help-blurb">
						<b>Drag:</b> Pan X Axis (all charts)
					</div>
					<div className="stackplot-controls-help-blurb">
						<b>Ctrl+Scroll:</b> Zoom X Axis (all charts)
					</div>
					<div className="stackplot-controls-help-blurb">
						<b>Shift+Drag</b> Pan Y Axis (single chart)
					</div>
					<div className="stackplot-controls-help-blurb">
						<b>Shift+Scroll:</b> Zoom Y Axis (single chart)
					</div>
					<div className="stackplot-controls-help-blurb">
						<b>Shift+Dbl Click:</b> Reset Y Axis (single chart)
					</div>
				</div>
				<div id="validation-plot-wrapper">
					<ValidationPlot
						data={stackplotData}
						config={stackPlotConfig}
						dateRange={dateRange}
						stackPlotRef={stackPlotRef}
						bubbleChartRefs={setChartRefs}
						callbacks={callbacks}
						highlighted={highlighted}
						setHighlighted={setHighlighted}
						scrollTableOnCursor={scrollTableOnCursor}
						onClearCursors={onClearCursors}
						autoYAxis={autoYAxis}
						fixedYScales={fixedYScales}
						zoomLevel={zoomLevel}
						parCodeIntervalMap={parCodeIntervalMap}
					/>
				</div>
			</>
		);
	};

	const handlePopout = () => {
		if (popoutWindow) return;
		const width = window.screen.availWidth;
		const height = window.screen.availHeight;
		const newWindow = window.open(
			"",
			"",
			`width=${width - 100}, height=${height - 100}`,
		);

		if (!newWindow) {
			alert("Popup blocked! Please allow popups for this site.");
			return;
		}

		// Optional: Copy parent styles to the new window so CSS continues working
		Array.from(document.styleSheets).forEach((styleSheet) => {
			try {
				if (styleSheet.cssRules) {
					const newStyleEl =
						newWindow.document.createElement("style");
					Array.from(styleSheet.cssRules).forEach((rule) => {
						newStyleEl.appendChild(
							newWindow.document.createTextNode(rule.cssText),
						);
					});
					newWindow.document.head.appendChild(newStyleEl);
				}
			} catch (e) {
				// Core cross-origin stylesheets fallback
				if (styleSheet.href) {
					const newLinkEl = newWindow.document.createElement("link");
					newLinkEl.rel = "stylesheet";
					newLinkEl.href = styleSheet.href;
					newWindow.document.head.appendChild(newLinkEl);
				}
			}
		});

		const contentDiv = document.createElement("div");

		newWindow.document.body.appendChild(contentDiv);

		newWindow.addEventListener("beforeunload", () => setPopoutWindow(null));

		setPopoutWindow(contentDiv);
	};

	return (
		<DataValidationContext.Provider
			value={{
				dateRange,
				updateTimeframe,
				parCodeList,
				setParCodeList,
				parCodeRoundingMap,
				parCodeIntervalMap,
				selectedPars,
				setSelectedPars,
				handleMinDate,
				handleMaxDate,
				getMinDate,
				getMaxDate,
				doXScroll,
				loading,
				dataChanges,
				setDataChanges,
				dataChanged,
				setDataChanged,
				optionsChanged,
				setOptionsChanged,
				saveChanges,
				autoValidate,
				autoValidateAll,
				highlighted,
				setHighlighted,
				dataToSortedMap,
				sortedToDataMap,
				callbacks,
				dataType,
				setDataType,
				calTypes,
				setCalTypes,
				getRowKey,
				isHighlighted,
			}}
		>
			<div id="data-validation">
				<ValidationControls
					selectedSite={selectedSite}
					beginLoad={beginLoad}
					toggleSortModal={toggleSortModal}
					toggleFilterModal={toggleFilterModal}
					errorCode={errorCode}
					setErrorCode={setErrorCode}
					blankFill={blankFill}
				/>
				<div id="validation-content-wrapper">
					{getPlot()}
					<ValidationTable
						data={sortedData ? sortedData : data}
						virtuosoRef={virtuosoRef}
						sortColumns={sortColumns}
						handleHeaderSort={handleHeaderSort}
						filterString={filterString}
						toggleInsertModal={toggleInsertModal}
					/>
				</div>

				<LoadingScreen
					hidden={!loading}
					loadingProgress={loadingProgress}
					isMobile={checkMobile()}
				/>
				<DataSortModal
					isOpen={openModal === "validation-sort-modal"}
					toggle={toggleSortModal}
					sortData={sortData}
					sortColumns={sortColumns}
					setSortColumns={setSortColumns}
				/>
				<ValidationFilterModal
					isOpen={openModal === "validation-filter-modal"}
					toggle={toggleFilterModal}
					applyFilters={setFilterFns}
					setFilterString={setFilterString}
				/>
				<ValidationInsertModal
					isOpen={openModal === "validation-insert-modal"}
					toggle={toggleInsertModal}
					onSubmit={insertRows}
					selectedSite={selectedSite}
				/>
				<ValidationPopover
					selectedSite={selectedSite}
					registerCallback={registerCallback}
					highlighted={highlighted}
					applyValidationInfo={applyValidationInfo}
					applyBatchChange={applyBatchChange}
				/>
				{popoutWindow
					? createPortal(
							<div id="data-validation" className='validation-popout'>
								<div id="validation-popout-content-wrapper">
									<div className="stackplot-y-axis-toggle-wrapper">
										<PlotZoomControls
											zoomLevel={zoomLevel}
											setZoomLevel={setZoomLevel}
										/>
										<label>Auto Y-Axis</label>
										<input
											type="checkbox"
											checked={autoYAxis}
											onClick={toggleAutoYAxis}
											readOnly
										/>
									</div>
									<div id="stackplot-controls-help">
										<div className="stackplot-controls-help-blurb">
											<b>Drag:</b> Pan X Axis (all charts)
										</div>
										<div className="stackplot-controls-help-blurb">
											<b>Ctrl+Scroll:</b> Zoom X Axis (all
											charts)
										</div>
										<div className="stackplot-controls-help-blurb">
											<b>Shift+Drag</b> Pan Y Axis (single
											chart)
										</div>
										<div className="stackplot-controls-help-blurb">
											<b>Shift+Scroll:</b> Zoom Y Axis
											(single chart)
										</div>
										<div className="stackplot-controls-help-blurb">
											<b>Shift+Dbl Click:</b> Reset Y Axis
											(single chart)
										</div>
									</div>
									<div id="validation-plot-wrapper">
										<ValidationPlot
											data={stackplotData}
											config={stackPlotConfig}
											dateRange={dateRange}
											stackPlotRef={stackPlotRef}
											bubbleChartRefs={setChartRefs}
											callbacks={callbacks}
											highlighted={highlighted}
											setHighlighted={setHighlighted}
											scrollTableOnCursor={
												scrollTableOnCursor
											}
											onClearCursors={onClearCursors}
											autoYAxis={autoYAxis}
											fixedYScales={fixedYScales}
											zoomLevel={zoomLevel}
											isPopout={true}
											parCodeIntervalMap={parCodeIntervalMap}
										/>
									</div>
								</div>
								
							<ValidationPopover
								selectedSite={selectedSite}
								registerCallback={registerCallback}
								highlighted={highlighted}
								applyValidationInfo={applyValidationInfo}
								applyBatchChange={applyBatchChange}
								isPopout={true}
							/>
							</div>,
							popoutWindow,
						)
					: null}
			</div>
		</DataValidationContext.Provider>
	);
}

function ValidationControls({
	selectedSite,
	beginLoad,
	toggleSortModal,
	toggleFilterModal,
	errorCode,
	blankFill,
}) {
	const [errorTooltip, setErrorTooltip] = useState(false);
	const {
		dateRange,
		updateTimeframe,
		handleMinDate,
		handleMaxDate,
		getMinDate,
		getMaxDate,
		selectedOptions,
		doXScroll,
		loading,
		dataChanged,
		optionsChanged,
		saveChanges,
		autoValidate,
		autoValidateAll,
		selectedPars,
		dataType,
		setDataType,
		calTypes,
		setCalTypes,
	} = useContext(DataValidationContext);

	const toggleErrorTooltip = () => {
		if (!!errorCode) {
			setErrorTooltip(!errorTooltip);
		} else {
			setErrorTooltip(false);
		}
	};

	const getErrorMessage = () => {
		switch (errorCode) {
			case 20001:
				return "Validation Codes not starting with 'V' must have Validated Val of -999";
			case 20002:
				return "Validated Val of -999 must not have Validation Code starting with 'V'";
			case 20003:
				return "Raw Val and Validated Val should match for Validation Code 'V'";
			case 20004:
				return "Control Val must not be empty for Validation Code 'VA'";
			case 20005:
				return "Data is locked and cannot be updated.";
			case 20006:
				return "Raw Val cannot be edited unless Source Code is D.";
			default:
				return "Oracle Error";
		}
	};

	const datesInSameMonth = () => {
		if (!dateRange?.min || !dateRange?.max) return false;
		const start = moment(dateRange.min + "+00:00").utcOffset(0, false),
			end = moment(dateRange.max + "+00:00").utcOffset(0, false);
		const inSameMonth =
			start.year() === end.year() &&
			start.month() === end.month() &&
			start < end;
		return inSameMonth;
	};

	const handleDataTypeSelect = (event) => {
		const value = event.target.value;
		setDataType(value);
	};

	return (
		<div id="validation-controls">
			<div id="validation-controls-left">
				<div id="validation-controls-left-top">
					<div className="stackplot-control-wrapper">
						<Label>Timeframe:</Label>
						<StackPlotTimeFrameSelector
							updateTimeframe={updateTimeframe}
						/>
					</div>
					<select
						id="validation-data-type-select"
						value={dataType}
						onChange={handleDataTypeSelect}
					>
						<option value="primary">Primary Data</option>
						<option value="cal">Calibration Data</option>
					</select>
					<CalTypeSelector
						hidden={dataType !== "cal"}
						calTypes={calTypes}
						setCalTypes={setCalTypes}
					/>
				</div>
				<div id="validation-controls-left-bottom">
					<ParCodeSelector selectedSite={selectedSite} />

					<Button
						id="validation-load-button"
						onClick={beginLoad}
						disabled={!optionsChanged}
					>
						Load Data
					</Button>
					<UncontrolledDropdown id="auto-validate-dropdown">
						<DropdownToggle
							id="auto-validate-button"
							style={{ position: "relative", top: "-1px" }}
							caret
						>
							Auto Validate
						</DropdownToggle>
						<DropdownMenu>
							<DropdownItem
								onClick={autoValidate}
								disabled={
									!datesInSameMonth() ||
									optionsChanged ||
									!selectedPars?.length
								}
							>
								Selected Pars Only
							</DropdownItem>
							<DropdownItem
								onClick={autoValidateAll}
								disabled={!datesInSameMonth()}
							>
								All Pars
							</DropdownItem>
						</DropdownMenu>
					</UncontrolledDropdown>

					<ButtonGroup>
						<Button
							id="validation-sort-modal-toggle"
							onClick={toggleSortModal}
						>
							Sort
						</Button>
						<Button
							id="validation-filter-modal-toggle"
							onClick={toggleFilterModal}
						>
							Filter
						</Button>
					</ButtonGroup>

					<Button
						id="blank-fill-toggle"
						disabled={dataType === "cal"}
					>
						Blank Fill
					</Button>
					<UncontrolledPopover
						target="blank-fill-toggle"
						placement="bottom"
					>
						<PopoverBody>
							<BlankFillControl
								blankFill={blankFill}
								selectedSite={selectedSite}
							/>
						</PopoverBody>
					</UncontrolledPopover>

					<Button
						id="validation-save-button"
						className="submit-button"
						className={!!errorCode ? "error" : ""}
						onClick={saveChanges}
						disabled={!dataChanged}
					>
						Save Changes
					</Button>

					<Tooltip
						isOpen={errorTooltip}
						target="validation-save-button"
						placement="bottom"
						toggle={toggleErrorTooltip}
					>
						{getErrorMessage(errorCode)}
					</Tooltip>
				</div>
			</div>

			<div id="x-zoom-controls" className="stackplot-control-wrapper">
				<Label>View:</Label>
				<input
					type="datetime-local"
					onChange={handleMinDate}
					min={getMinDate()}
					max={dateRange.max || getMaxDate()}
					step={
						selectedOptions
							? selectedOptions.precision.match(/\d+-min/)
								? 60
								: 3600
							: 1
					}
					value={dateRange.min || ""}
				/>
				<span>-</span>
				<input
					type="datetime-local"
					onChange={handleMaxDate}
					min={dateRange.min || getMinDate()}
					max={getMaxDate()}
					step={
						selectedOptions
							? selectedOptions.precision.match(/\d+-min/)
								? 60
								: 3600
							: 1
					}
					value={dateRange.max || ""}
				/>
				<DateScroller doXScroll={doXScroll} />
			</div>
			<Button
				id="stackplots-refresh-button"
				onClick={beginLoad}
				disabled={loading}
			>
				{utilIcons.refresh(colors["ars-neutral-900"])}
			</Button>
		</div>
	);
}

function StackPlotTimeFrameSelector({ updateTimeframe }) {
	const [dateRange, setDateRange] = useState({ min: null, max: null });

	useEffect(() => {
		init();
	}, []);

	const init = () => {
		const newDateRange = {
			min: moment()
				.set("hour", 0)
				.set("minute", 0)
				.set("second", 0)
				.set("millisecond", 0)
				.set("date", 1)
				.subtract(1, "month")
				.format("YYYY-MM-DDTHH:mm"),
			max: moment()
				.set("hour", 0)
				.set("minute", 0)
				.set("second", 0)
				.set("millisecond", 0)
				.set("date", 1)
				.subtract(1, "minute")
				.format("YYYY-MM-DDTHH:mm"),
		};
		setDateRange(newDateRange);
		updateTimeframe(newDateRange.min, newDateRange.max);
	};

	const fireUpdate = (event) => {
		const allowedIds = [
			"stackplot-timeframe-numdays-input",
			"stackplot-timeframe-mindate-input",
			"stackplot-timeframe-maxdate-input",
			"stackplot-timeframe-mode-select",
		];
		if (
			event.relatedTarget?.id &&
			allowedIds.includes(event.relatedTarget.id)
		)
			return;
		if (dateRange.min && dateRange.max) {
			updateTimeframe(dateRange.min, dateRange.max);
		}
	};

	const handleMinDate = (event) => {
		const value = event.target.value;
		setDateRange((values) => ({ ...values, min: value }));
	};

	const handleMaxDate = (event) => {
		const value = event.target.value;
		setDateRange((values) => ({ ...values, max: value }));
	};

	return (
		<div id="stackplot-timeframe-selector">
			<input
				id="stackplot-timeframe-mindate-input"
				type="datetime-local"
				value={dateRange.min}
				onChange={handleMinDate}
				step={3600}
				onBlur={fireUpdate}
			/>
			<span>to</span>
			<input
				id="stackplot-timeframe-maxdate-input"
				type="datetime-local"
				value={dateRange.max}
				onChange={handleMaxDate}
				onBlur={fireUpdate}
			/>
		</div>
	);
}

function DateScroller({ doXScroll }) {
	const [quantity, setQuantity] = useState(1);
	const [unit, setUnit] = useState("days");

	const handleQuantity = (event) => {
		const value = Number(event.target.value);
		setQuantity(value);
	};

	const handleUnit = (event) => {
		const value = event.target.value;
		setUnit(value);
	};

	const handleMinus = () => {
		doXScroll({
			direction: "left",
			quantity,
			unit,
		});
	};

	const handlePlus = () => {
		doXScroll({
			direction: "right",
			quantity,
			unit,
		});
	};

	return (
		<div className="date-scroller">
			<Button onClick={handleMinus}>-</Button>
			<input
				className="time-quantity-input"
				type="number"
				step={1}
				value={quantity}
				onChange={handleQuantity}
			/>
			<select
				className="time-unit-select"
				value={unit}
				onChange={handleUnit}
			>
				<option value="hours">Hour(s)</option>
				<option value="days">Day(s)</option>
				<option value="weeks">Week(s)</option>
				<option value="months">Month(s)</option>
			</select>
			<Button onClick={handlePlus}>+</Button>
		</div>
	);
}

function ParCodeSelector({ selectedSite }) {
	const [selectedParCodeList, setSelectedParCodeList] = useState([]);
	const [allParCodes, setAllParCodes] = useState(false);
	const [isOpen, setIsOpen] = useState(false);

	const ref = useRef(null);

	const { sendGetRequest, sendPostRequest } = useContext(RestContext);

	const {
		dateRange,
		parCodeList,
		setParCodeList,
		parCodeRoundingMap,
		parCodeIntervalMap,
		setSelectedPars,
		dataType,
		calTypes,
	} = useContext(DataValidationContext);

	const toggleOpen = () => setIsOpen(!isOpen);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (ref.current && isOpen) {
				if (
					!ref.current.contains(event.target) &&
					event.target.id !== "site-select"
				) {
					toggleOpen();
				}
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [ref, isOpen, toggleOpen]);

	useEffect(() => {
		if (allParCodes) {
			sendGetRequest(VALIDATION_URL + "/parameters/all", (response) => {
				parCodeRoundingMap.clear();
				parCodeIntervalMap.clear();
				for (let par of response.data) {
					parCodeRoundingMap.set(par.id.parCode, {
						prec: par.rawPrec,
						trunc: !!par.truncVal,
					});
					parCodeIntervalMap.set(par.id.parCode, par.interval);
				}
				setParCodeList(response.data);
			});
		} else {
			if (dateRange?.min && dateRange?.max) {
				if (dataType === "cal") {
					const payload = {
						siteId: selectedSite,
						calTypes: calTypes,
						minDate: deTzTimestamp(dateRange.min),
						maxDate: deTzTimestamp(dateRange.max),
					};
					sendPostRequest(
						VALIDATION_URL + "/parameters/cal",
						payload,
						(response) => {
							parCodeRoundingMap.clear();
							for (let par of response.data) {
								parCodeRoundingMap.set(par.id.parCode, {
									prec: par.rawPrec,
									trunc: !!par.truncVal,
								});
							}
							setParCodeList(response.data);
						},
					);
				} else {
					const payload = {
						siteId: selectedSite,
						minDate: deTzTimestamp(dateRange.min),
						maxDate: deTzTimestamp(dateRange.max),
					};
					sendPostRequest(
						VALIDATION_URL + "/parameters",
						payload,
						(response) => {
							parCodeRoundingMap.clear();
							parCodeIntervalMap.clear();
							for (let par of response.data) {
								parCodeRoundingMap.set(par.id.parCode, {
									prec: par.rawPrec,
									trunc: !!par.truncVal,
								});
								parCodeIntervalMap.set(par.id.parCode, par.interval);
							}

							setParCodeList(response.data);
						},
					);
				}
			}
		}
	}, [selectedSite, allParCodes, dataType, calTypes, dateRange]);

	useEffect(() => {
		const newSelected = [];
		for (let par of selectedParCodeList) {
			const foundPar = parCodeList.find(
				(other) => other.parCode === par.parCode,
			);
			if (foundPar) newSelected.push(foundPar);
		}
		newSelected.sort(compPars);
		setSelectedParCodeList(newSelected);
	}, [parCodeList]);

	useEffect(() => {
		setSelectedPars(selectedParCodeList);
	}, [selectedParCodeList]);

	const compPars = (a, b) => {
		return parCodeList.indexOf(a) - parCodeList.indexOf(b);
	};

	const togglePar = (par) => {
		const newSelected = [...selectedParCodeList];
		const foundPar = selectedParCodeList.find(
			(other) => other.parCode === par.parCode,
		);
		if (foundPar) {
			newSelected.splice(newSelected.indexOf(foundPar), 1);
		} else {
			newSelected.push(par);
		}
		newSelected.sort(compPars);
		setSelectedParCodeList(newSelected);
	};

	const getSelectedList = () => {
		if (!selectedParCodeList.length) {
			return <span className="no-pars-selected">Select Parameters</span>;
		} else {
			return selectedParCodeList.map((par) => par.parCode).join(", ");

			// return `${selectedParCodeList.length} selected`;
		}
	};

	const isSelected = (par) => {
		return selectedParCodeList.some(
			(other) => other.parCode === par.parCode,
		);
	};

	const reorder = (index, newIndex) => {
		if (
			index < 0 ||
			index >= parCodeList.length ||
			newIndex < 0 ||
			newIndex >= parCodeList.length
		)
			return;
		const newList = [...parCodeList];
		newList[index] = parCodeList[newIndex];
		newList[newIndex] = parCodeList[index];
		setParCodeList(newList);
	};

	const mapParCodesToList = () => {
		return parCodeList.map((par, index) => (
			<ParCodeListEntry
				par={par}
				parIndex={index}
				isSelected={isSelected(par)}
				togglePar={togglePar}
				reorder={reorder}
			/>
		));
	};

	const selectAll = () => {
		const newSelected = [...parCodeList];
		newSelected.sort(compPars);
		setSelectedParCodeList(newSelected);
	};

	const unselectAll = () => {
		setSelectedParCodeList([]);
	};

	return (
		<div
			id="validation-par-code-selector-wrapper"
			className="stackplot-control-wrapper"
			ref={ref}
		>
			<div id="validation-par-code-selector">
				<div id="par-code-selector-display" onClick={toggleOpen}>
					{getSelectedList()}
				</div>
				<Collapse id="par-code-selector-collapse" isOpen={isOpen}>
					<div id="par-code-selector-list-container">
						{mapParCodesToList()}
					</div>
					<div id="par-code-selector-button-container">
						<Button onClick={selectAll}>Select All</Button>
						<Button onClick={unselectAll}>Clear</Button>
					</div>
				</Collapse>
			</div>
		</div>
	);
}

function CalTypeSelector({ hidden, calTypes, setCalTypes }) {
	const [isOpen, setIsOpen] = useState(false);

	const ref = useRef(null);

	const toggleOpen = () => setIsOpen(!isOpen);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (ref.current && isOpen) {
				if (
					!ref.current.contains(event.target) &&
					event.target.id !== "site-select"
				) {
					toggleOpen();
				}
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [ref, isOpen, toggleOpen]);

	const toggleType = (type) => {
		const newSelected = [...calTypes];
		const index = calTypes.indexOf(type);
		if (index !== -1) {
			newSelected.splice(index, 1);
		} else {
			newSelected.push(type);
		}
		setCalTypes(newSelected);
	};

	const getSelectedList = () => {
		if (!calTypes.length) {
			return (
				<span className="no-cal-types-selected">Select Cal Types</span>
			);
		} else {
			return calTypes.join(", ");
		}
	};

	const isSelected = (type) => {
		return calTypes.includes(type);
	};

	const mapCalTypesToList = () => {
		return CAL_TYPES.map((type, index) => (
			<div
				className="validation-cal-type-list-entry"
				onClick={() => toggleType(type)}
			>
				<input type="checkbox" checked={isSelected(type)} readOnly />
				<div className="validation-cal-type-list-entry-text">
					{type}
				</div>
			</div>
		));
	};

	const selectAll = () => {
		setCalTypes([...CAL_TYPES]);
	};

	const unselectAll = () => {
		setCalTypes([]);
	};

	return (
		<div
			id="validation-cal-type-selector-wrapper"
			className="stackplot-control-wrapper"
			ref={ref}
			hidden={hidden}
		>
			<div id="validation-cal-type-selector">
				<div id="cal-type-selector-display" onClick={toggleOpen}>
					{getSelectedList()}
				</div>
				<Collapse id="cal-type-selector-collapse" isOpen={isOpen}>
					<div id="cal-type-selector-list-container">
						{mapCalTypesToList()}
					</div>
					<div id="cal-type-selector-button-container">
						<Button onClick={selectAll}>Select All</Button>
						<Button onClick={unselectAll}>Clear</Button>
					</div>
				</Collapse>
			</div>
		</div>
	);
}

function ParCodeListEntry({ par, parIndex, isSelected, togglePar, reorder }) {
	const getAbbr = () => {
		if (!par?.parAbbr) return "";
		return par.parAbbr;
	};

	const handleClick = () => {
		togglePar(par);
	};

	return (
		<div className="validation-par-code-list-entry" onClick={handleClick}>
			<SortControls size="small" index={parIndex} updateFn={reorder} />
			<input type="checkbox" checked={isSelected} readOnly />
			<div className="validation-par-code-list-entry-abbr">
				{getAbbr()}
			</div>
		</div>
	);
}

function ValidationTable({
	data,
	virtuosoRef,
	sortColumns,
	handleHeaderSort,
	filterString,
	toggleInsertModal,
}) {
	const [size, setSize] = useState("half");

	const anchorIndex = useRef(null);
	const lastClickedIndex = useRef(null);

	const {
		dataChanges,
		setDataChanges,
		setDataChanged,
		sortedToDataMap,
		highlighted,
		setHighlighted,
		callbacks,
		parCodeRoundingMap,
		dataType,
		getRowKey,
		isHighlighted,
	} = useContext(DataValidationContext);

	const getHeaders = () => {
		if (!data.length) return null;
		const columnsToUse =
			dataType == "cal" ? CAL_AQMET_COLUMNS : AQMET_COLUMNS;
		return (
			<tr>
				<th className="validation-table-row-select">
					<input
						type="checkbox"
						checked={anyHighlighted()}
						onClick={handleHeaderCheckboxClick}
						readOnly
					/>
				</th>
				{Object.keys(columnsToUse).map((column) => (
					<th
						id={`validation-table-header-${column.replace(/([A-Z])/, "-$1").toLowerCase()}`}
					>
						<div>
							{columnsToUse[column]}
							<HeaderSortButtons
								sortValue={column}
								sortCallback={handleHeaderSort}
								currentSort={sortColumns}
							/>
						</div>
					</th>
				))}
			</tr>
		);
	};

	const anyHighlighted = () => {
		return Object.keys(highlighted).length > 0;
	};

	const handleHeaderCheckboxClick = (event) => {
		const newHighlighted = {};
		if (!anyHighlighted()) {
			for (let row of data) {
				newHighlighted[row.recordDate] =
					newHighlighted[row.recordDate] || [];
				newHighlighted[row.recordDate].push(getRowKey(row));
			}
		}
		setHighlighted(newHighlighted);
	};

	const handleCheckboxClick = (event, row, index) => {
		event.preventDefault();
		event.stopPropagation();
		if (event.ctrlKey && event.shiftKey) {
			return;
		} else if (event.ctrlKey) {
			// Select multiple individual cells
			handleMultipleRowSelect(event, row, index);
		} else if (event.shiftKey) {
			// Select range of rows
			handleRangeRowSelect(event, row, index);
		} else {
			// Select single row
			handleSingleRowSelect(event, row, index);
		}
	};

	const hasKey = (pars, rowKey) => {
		if (Array.isArray(rowKey)) {
			return includesArray(pars, rowKey);
		} else {
			return pars.includes(rowKey);
		}
	};

	const removeHighlightedPar = (pars, rowKey) => {
		if (Array.isArray(rowKey)) {
			const matched = pars.find((a) => compArray(a, rowKey));
			pars.splice(pars.indexOf(matched), 1);
		} else {
			pars.splice(pars.indexOf(rowKey), 1);
		}
	};

	const handleSingleRowSelect = (event, row, index) => {
		let newPars = highlighted[row.recordDate] || [];
		let newHighlighted;
		if (hasKey(newPars, getRowKey(row))) {
			newHighlighted = { ...highlighted };
			removeHighlightedPar(newPars, getRowKey(row));
		} else {
			newHighlighted = {};
			newPars.push(getRowKey(row));
		}
		if (newPars.length) {
			newHighlighted[row.recordDate] = newPars;
		} else {
			delete newHighlighted[row.recordDate];
		}
		setHighlighted(newHighlighted);
		lastClickedIndex.current = index;
		anchorIndex.current = index;
	};

	const handleMultipleRowSelect = (event, row, index) => {
		let newPars = highlighted[row.recordDate] || [];
		let newHighlighted = { ...highlighted };
		let rowKey = getRowKey(row);
		if (hasKey(newPars, rowKey)) {
			removeHighlightedPar(newPars, rowKey);
		} else {
			newPars.push(getRowKey(row));
		}
		if (newPars.length) {
			newHighlighted[row.recordDate] = newPars;
		} else {
			delete newHighlighted[row.recordDate];
		}
		setHighlighted(newHighlighted);
		lastClickedIndex.current = index;
		anchorIndex.current = index;
	};

	const handleRangeRowSelect = (event, row, index) => {
		let newHighlighted = {};
		const range = data.slice(
			Math.min(index, anchorIndex.current),
			Math.max(index, anchorIndex.current) + 1,
		);
		if (index === lastClickedIndex.current) {
			let clear = true;
			for (let row of range) {
				if (!isHighlighted(getRowKey(row), row.recordDate)) {
					clear = false;
					break;
				}
			}
			if (!clear) {
				for (let row of range) {
					newHighlighted[row.recordDate] =
						newHighlighted[row.recordDate] || [];
					newHighlighted[row.recordDate].push(getRowKey(row));
				}
			}
		} else {
			for (let row of range) {
				newHighlighted[row.recordDate] =
					newHighlighted[row.recordDate] || [];
				newHighlighted[row.recordDate].push(getRowKey(row));
			}
		}
		setHighlighted(newHighlighted);
		lastClickedIndex.current = index;
	};

	const formatColumn = (column, value, parCode) => {
		const formatFn = {
			recordDate: formatTimestamp,
			updateDate: formatTimestamp,
			insertDate: formatTimestamp,
			validatedVal: formatValidatedVal,
		};
		if (column in formatFn) {
			return formatFn[column](value, parCode);
		} else {
			return value;
		}
	};

	const formatTimestamp = (timestamp) => {
		return moment(timestamp).format("MM/DD/YYYY HH:mm");
	};

	const formatValidatedVal = (value, parCode) => {
		if (value === null) return value;
		if (!parCodeRoundingMap?.get(parCode)) return value;
		const { prec, trunc } = parCodeRoundingMap.get(parCode);
		if (trunc) {
			const fact = Math.pow(10, prec);
			return Math.trunc(fact * value) / fact;
		} else {
			return Number.parseFloat(value).toFixed(prec);
		}
	};

	const handleInputCellClick = (event) => {
		if (event.target.tagName !== "INPUT") {
			const el = event.target.querySelector("input");
			el.focus();
		}
	};

	const handleCellInput = (event, index, row, column) => {
		if (!document.activeElement === event.target) return;
		const value = event.target.value;
		const adjIndex = sortedToDataMap.current[index];
		setDataChanges((values) => ({
			...values,
			[adjIndex]: { ...row, [column]: value },
		}));
		setDataChanged(true);
	};

	return (
		<div id="validation-table-wrapper" className={size}>
			<div id="validation-table-controls">
				<span>Table View</span>
				<span
					id="validation-table-filter-string"
					hidden={filterString === ""}
				>
					{"Filters: " + filterString}
				</span>
				<Button
					onClick={toggleInsertModal}
					disabled={dataType !== "cal"}
				>
					+
				</Button>
				<Button
					onClick={() => {
						setSize("minimized");
					}}
				>
					{utilIcons.minimized(colors["ars-neutral-900"])}
				</Button>
				<Button
					onClick={() => {
						setSize("half");
					}}
				>
					{utilIcons.halfScreen(colors["ars-neutral-900"])}
				</Button>
				<Button
					onClick={() => {
						setSize("full");
					}}
				>
					{utilIcons.fullScreen(colors["ars-neutral-900"])}
				</Button>
			</div>
			<div id="validation-table">
				<TableVirtuoso
					ref={virtuosoRef}
					style={{ height: "100%" }}
					data={data}
					components={{
						TableRow: TableRow,
						Table: (props) => (
							<table
								{...props}
								style={{ ...props.style, tableLayout: "fixed" }}
							/>
						),
					}}
					fixedHeaderContent={getHeaders}
					context={{
						highlighted,
						callbacks,
						handleCheckboxClick,
						handleInputCellClick,
						dataChanges,
						formatColumn,
						handleCellInput,
						getRowKey,
						dataType,
						isHighlighted,
					}}
				/>
			</div>
		</div>
	);
}

const TableRow = forwardRef(({ item, context, ...props }, ref) => {
	const index = props["data-index"];
	const row = item;
	const {
		highlighted,
		callbacks,
		handleCheckboxClick,
		handleInputCellClick,
		dataChanges,
		formatColumn,
		handleCellInput,
		getRowKey,
		dataType,
		isHighlighted,
	} = context;
	return (
		<tr
			ref={ref}
			{...props}
			className={
				"validation-table-row" +
				(isHighlighted(getRowKey(row), row.recordDate)
					? " highlighted"
					: "")
			}
			onContextMenu={callbacks.openPopover}
		>
			<td
				className="validation-table-row-select"
				onMouseDown={(e) => handleCheckboxClick(e, row, index)}
			>
				<input
					type="checkbox"
					checked={
						isHighlighted(getRowKey(row), row.recordDate) || false
					}
					readOnly
				/>
			</td>
			{(dataType == "cal" ? CAL_COLUMN_LIST : COLUMN_LIST).map(
				(column) => {
					if (
						(dataType == "cal"
							? CAL_EDITABLE_COLUMNS
							: EDITABLE_COLUMNS)[column]
					) {
						return (
							<td
								className="validation-table-cell-editable"
								onClick={handleInputCellClick}
							>
								<input
									type="text"
									className={
										"validation-table-input" +
										(exists(dataChanges[index]?.[column]) &&
										dataChanges[index]?.[column] !==
											row[column]
											? " changed"
											: "")
									}
									placeholder={row[column]}
									onChange={(event) => {
										handleCellInput(
											event,
											index,
											ifExists(dataChanges[index], row),
											column,
										);
									}}
									value={dataChanges[index]?.[column]}
								/>
							</td>
						);
					} else {
						return (
							<td
								className={
									"validation-table-cell-readonly" +
									(exists(dataChanges[index]?.[column]) &&
									dataChanges[index]?.[column] !== row[column]
										? " changed"
										: "")
								}
							>
								{formatColumn(
									column,
									dataChanges[index]
										? dataChanges[index][column]
										: row[column],
									row.parCode,
								)}
							</td>
						);
					}
				},
			)}
		</tr>
	);
});

function LoadingScreen({ hidden, loadingProgress = 0, isMobile = false }) {
	const getContent = () => {
		if (isMobile) {
			return (
				<div className="loading-wrapper">
					<progress value={loadingProgress} />
					<span>{Math.round(loadingProgress * 1000) / 10}%</span>
				</div>
			);
		}
	};

	return (
		<div
			className={"loading" + (isMobile ? " mobile" : "")}
			hidden={hidden}
		>
			{getContent()}
		</div>
	);
}

function DataSortModal({
	isOpen,
	toggle,
	sortData,
	sortColumns,
	setSortColumns,
}) {
	const { dataType } = useContext(DataValidationContext);

	const getUnselectedColumns = () => {
		const unselectedColumns = Object.keys(
			dataType === "cal" ? CAL_AQMET_COLUMNS : AQMET_COLUMNS,
		).filter(
			(column) =>
				!sortColumns.some((sortColumn) => sortColumn[0] === column),
		);
		return unselectedColumns;
	};

	const mapSortColumnsToOption = () => {
		return sortColumns.map((sortColumn, index) => (
			<DataSortOption
				index={index}
				isLast={index === sortColumns.length - 1}
				sortColumns={sortColumns}
				setSortColumns={setSortColumns}
				createOption={createOption}
				deleteOption={deleteOption}
				dataType={dataType}
			/>
		));
	};

	const createOption = () => {
		const newSortColumns = [
			...sortColumns,
			[getUnselectedColumns()[0], "Asc"],
		];
		setSortColumns(newSortColumns);
	};

	const deleteOption = (index) => {
		const newSortColumns = [...sortColumns];
		newSortColumns.splice(index, 1);
		setSortColumns(newSortColumns);
	};

	const resetSortColumns = () => {
		setSortColumns([
			["recordDate", "Asc"],
			["parCode", "Asc"],
		]);
	};

	return (
		<Modal id="validation-sort-modal" isOpen={isOpen} toggle={toggle}>
			<ModalHeader toggle={toggle}>Sort Data</ModalHeader>
			<ModalBody>
				<div id="validation-sort-options-wrapper">
					<h4>Sort by:</h4>
					<div id="validation-sort-options-container">
						{mapSortColumnsToOption()}
					</div>
					<Button
						id="validation-sort-reset"
						onClick={resetSortColumns}
					>
						Reset Sort
					</Button>
				</div>
			</ModalBody>
		</Modal>
	);
}

function DataSortOption({
	index,
	isLast,
	sortColumns,
	setSortColumns,
	createOption,
	deleteOption,
	dataType,
}) {
	const getOptionValue = () => {
		const option = sortColumns[index];
		return option[0];
	};

	const getOptionDirection = () => {
		const option = sortColumns[index];
		return option[1];
	};

	const toggleDirection = () => {
		const newOption = [...sortColumns[index]];
		if (getOptionDirection() === "Asc") {
			newOption[1] = "Desc";
		} else {
			newOption[1] = "Asc";
		}
		const newSortColumns = [...sortColumns];
		newSortColumns[index] = newOption;
		setSortColumns(newSortColumns);
	};

	const getSuffix = () => {
		if (isLast) {
			return (
				<Button
					className="validation-sort-option-direction-toggle"
					onClick={createOption}
				>
					+
				</Button>
			);
		} else {
			return <span>then</span>;
		}
	};

	const mapUnselectedColumnsToOption = () => {
		return Object.keys(
			dataType === "cal" ? CAL_AQMET_COLUMNS : AQMET_COLUMNS,
		)
			.filter(
				(column) =>
					getOptionValue() === column ||
					!sortColumns.some((sortColumn) => sortColumn[0] === column),
			)
			.map((column) => (
				<option value={column}>
					{
						(dataType === "cal"
							? CAL_AQMET_COLUMNS
							: AQMET_COLUMNS)[column]
					}
				</option>
			));
	};

	const handleSelect = (event) => {
		const value = event.target.value;
		const newOption = [value, "Asc"];
		const newSortColumns = [...sortColumns];
		newSortColumns[index] = newOption;
		setSortColumns(newSortColumns);
	};

	return (
		<div className="validation-sort-option">
			<Button
				className="validation-sort-option-delete"
				disabled={sortColumns.length === 1}
				onClick={() => deleteOption(index)}
			>
				-
			</Button>
			<select value={getOptionValue()} onChange={handleSelect}>
				{mapUnselectedColumnsToOption()}
			</select>
			<Button
				className="validation-sort-option-direction"
				onClick={toggleDirection}
			>
				{getOptionDirection()}
			</Button>
			{getSuffix()}
		</div>
	);
}

function ValidationPopover({
	selectedSite,
	registerCallback,
	highlighted,
	applyValidationInfo,
	applyBatchChange,
	isPopout = false
}) {
	const [valCodeList, setValCodeList] = useState([]);
	const [selectedPars, setSelectedPars] = useState({});
	const [validationCode, setValidationCode] = useState("V");
	const [qualifierCode, setQualifierCode] = useState(null);
	const [controlVal, setControlVal] = useState(null);
	const [pos, setPos] = useState(null);
	const [visible, setVisible] = useState(false);
	const [expanded, setExpanded] = useState(false);

	const ref = useRef(null);

	const { sendGetRequest } = useContext(RestContext);
	const { parCodeList, isHighlighted, dataType } = useContext(
		DataValidationContext,
	);

	const POPOVER_WIDTH = 400;
	const POPOVER_HEIGHT = 250;

	const toggleExpanded = () => setExpanded(!expanded);

	useEffect(() => {
		registerCallback("openPopover" + (isPopout ? "Popout" : ""), handleOpenEvent);
	}, []);

	useEffect(() => {
		sendGetRequest(
			VALIDATION_URL +
				"/valCodes/" +
				(dataType === "cal" ? "cal/" : "") +
				selectedSite,
			(response) => {
				const responseData = response.data;
				responseData.sort((a, b) =>
					a.validationCode > b.validationCode ? 1 : -1,
				);
				setValCodeList(response.data);
			},
		);
	}, [selectedSite, dataType]);

	useEffect(() => {
		setSelectedPars(
			Object.fromEntries(
				getHighlightedPars().map((par) => [par.parCode, true]),
			),
		);
		setValidationCode("V");
		setQualifierCode(null);
		setControlVal(null);
	}, [highlighted]);

	useEffect(() => {
		if (!ref.current) return;
		const doc = ref.current.ownerDocument;
		const handleClickOutside = (event) => {
			if (ref.current && pos) {
				if (!ref.current.contains(event.target)) {
					setPos(null);
				}
			}
		};

		doc.addEventListener("mousedown", handleClickOutside);
		return () => {
			doc.removeEventListener("mousedown", handleClickOutside);
		};
	}, [ref, pos]);

	useEffect(() => {
		if (!pos) {
			setValidationCode("V");
			setQualifierCode(null);
			setControlVal(null);
			setExpanded(false);
		}
	}, [pos]);

	useEffect(() => {
		setVisible(!!highlighted && Object.keys(highlighted).length);
	}, [highlighted]);

	useEffect(() => {
		if (pos && expanded) {
			setPos({
				x: clamp(
					pos.x,
					10,
					window.innerWidth - (POPOVER_WIDTH * 2 + 44),
				),
				y: pos.y,
			});
		}
	}, [expanded]);

	const handleOpenEvent = (event) => {
		if (!event.shiftKey) {
			event.preventDefault();
			setPos({
				x: clamp(
					event.clientX - POPOVER_WIDTH / 2,
					10,
					window.innerWidth - (POPOVER_WIDTH + 44),
				),
				y: clamp(
					event.clientY - POPOVER_HEIGHT / 2,
					10,
					window.innerHeight - (POPOVER_HEIGHT + 10),
				),
			});
		}
	};

	const getHighlightedPars = () => {
		if (!parCodeList || !highlighted) return [];
		const highlightedParCodes = [];
		Object.keys(highlighted).forEach((timestamp) => {
			for (let parCode of highlighted[timestamp]) {
				if (Array.isArray(parCode)) parCode = parCode[0];
				if (!highlightedParCodes.includes(parCode)) {
					highlightedParCodes.push(parCode);
				}
			}
		});
		const highlightedPars = [];
		highlightedParCodes.forEach((parCode) => {
			const par = parCodeList.find(
				(foundPar) => foundPar.parCode === parCode,
			);
			if (par) {
				highlightedPars.push(par);
			}
		});
		return highlightedPars;
	};

	const getXPos = () => {
		const x = pos?.x || 0;
		return x;
	};

	const getYPos = () => {
		return pos?.y || 0;
	};

	const isChecked = (parCode) => {
		if (!selectedPars) return false;
		return selectedPars[parCode];
	};

	const toggleParCode = (parCode) => {
		setSelectedPars((values) => ({
			...values,
			[parCode]: !selectedPars[parCode],
		}));
	};

	const mapParCodesToList = () => {
		return getHighlightedPars().map((par) => (
			<div
				className="validation-popover-parcode"
				onClick={() => toggleParCode(par.parCode)}
			>
				<input
					type="checkbox"
					checked={isChecked(par.parCode)}
					readOnly
				/>
				<span>{par.parAbbr}</span>
			</div>
		));
	};

	const mapValCodesToList = () => {
		if (!valCodeList) return null;
		return valCodeList.map((valCode) => (
			<option
				value={valCode.validationCode}
			>{`${valCode.validationCode} - ${valCode.reason}`}</option>
		));
	};

	const isAdjusted = () => {
		return validationCode === "VA" || validationCode === "VM";
	};

	const isValidControlVal = () => {
		return !!controlVal?.match(/^[+*/=]?\-?\d+(\.\d+)?$/);
	};

	const handleTextInput = (event) => {
		const name = event.target.name;
		const value = event.target.value;
		if (name === "validationCode") {
			setValidationCode(value);
		} else if (name === "qualifierCode") {
			setQualifierCode(value);
		} else if (name === "controlVal") {
			setControlVal(value);
		}
	};

	const handleApply = () => {
		const payload = {
			selectedPars,
			validationCode,
			qualifierCode,
			controlVal,
		};
		applyValidationInfo(payload);
	};

	const canApply = () => {
		return !(
			(validationCode === "VA" && !isValidControlVal()) ||
			(validationCode === "VM" && isNaN(controlVal))
		);
	};

	return (
		<div
			id="validation-popover"
			ref={ref}
			hidden={!pos || !visible}
			style={{
				top: getYPos(),
				left: getXPos(),
				width: POPOVER_WIDTH * (expanded ? 2 : 1),
				height: POPOVER_HEIGHT,
			}}
		>
			<div id="validation-popover-main">
				<div id="validation-popover-left">
					<Label>Parameters</Label>
					<div id="validation-popover-parcode-container">
						{mapParCodesToList()}
					</div>
				</div>
				<div id="validation-popover-right">
					<div className="validation-popover-input-wrapper">
						<Label>Validation Code</Label>
						<select
							name={"validationCode"}
							value={validationCode}
							onChange={handleTextInput}
						>
							{mapValCodesToList()}
						</select>
					</div>
					<div className="validation-popover-input-wrapper">
						<Label>
							{dataType === "cal"
								? "Comment"
								: "Qualifier Code(s)"}
						</Label>
						<input
							type="text"
							name="qualifierCode"
							value={qualifierCode || ""}
							onChange={handleTextInput}
						/>
					</div>
					<div
						className="validation-popover-input-wrapper"
						hidden={!isAdjusted()}
					>
						<Label>
							{validationCode === "VM"
								? "Maximum Value"
								: "Control Val"}
						</Label>
						<input
							type="text"
							name="controlVal"
							value={controlVal || ""}
							onChange={handleTextInput}
							pattern="^[+*/=]?\-?\d+(\.\d+)?$"
						/>
					</div>
					<Button
						id="validation-popover-apply-button"
						onClick={handleApply}
						disabled={!canApply()}
					>
						Apply
					</Button>
				</div>
			</div>
			<div id="validation-popover-extra" hidden={!expanded}>
				<Label>Batch Edit</Label>
				<BatchEditControl
					text={dataType === "cal" ? "Cal Val" : "Raw Val"}
					name={dataType === "cal" ? "calVal" : "rawVal"}
					type={"number"}
					handleBatchApply={applyBatchChange}
					inputWidth={8}
				/>
				<BatchEditControl
					text={dataType === "cal" ? "Comment" : "Qualifier Code"}
					name={dataType === "cal" ? "comment" : "qualifierCode"}
					type={"text"}
					handleBatchApply={applyBatchChange}
				/>
				<BatchEditControl
					text={"Source Code"}
					name={"sourceCode"}
					type={"text"}
					handleBatchApply={applyBatchChange}
					inputWidth={2}
					hidden={dataType === "cal"}
				/>
				<BatchEditControl
					text={"Status"}
					name={"status"}
					type={"text"}
					handleBatchApply={applyBatchChange}
					inputWidth={2}
					hidden={dataType === "cal"}
				/>
			</div>
			<Button
				id="validation-popover-expand-button"
				onClick={toggleExpanded}
			>
				{utilIcons.caret(colors["ars-neutral-900"], {
					rotate: (expanded ? "" : "-") + "90deg",
				})}
			</Button>
		</div>
	);
}

function BatchEditControl({
	text,
	name,
	type,
	handleBatchApply,
	inputWidth,
	hidden,
}) {
	const [value, setValue] = useState(null);

	const handleInput = (event) => {
		setValue(event.target.value || null);
	};

	return (
		<div className="validation-popover-batch-edit-wrapper" hidden={hidden}>
			<Label>{text}</Label>
			<input
				style={{
					width: exists(inputWidth)
						? `calc(8px + ${inputWidth}ch)`
						: undefined,
				}}
				type={type}
				value={value}
				onChange={handleInput}
			/>
			<Button onClick={() => handleBatchApply(name, value)}>
				{utilIcons.caret(colors["ars-neutral-900"], {
					rotate: "-90deg",
				})}
			</Button>
		</div>
	);
}

function ValidationFilterModal({
	isOpen,
	toggle,
	applyFilters,
	setFilterString,
}) {
	const [filters, setFilters] = useState([]);

	const { dataType } = useContext(DataValidationContext);

	const buildFilterFn = (filter) => {
		const { column, operation, value } = filter;
		switch (operation) {
			case "=":
				return (row) => Number(row[column]) === Number(value);
			case "<":
				return (row) => Number(row[column]) < Number(value);
			case ">":
				return (row) => Number(row[column]) > Number(value);
			case "<=":
				return (row) => Number(row[column]) <= Number(value);
			case ">=":
				return (row) => Number(row[column]) >= Number(value);
			case "is":
				return (row) => String(row[column]) === String(value);
			case "is not":
				return (row) => String(row[column]) != String(value);
			case "is in":
				let a = String(value).split(",");
				return (row) => a.contains(String(row[column]));
			case "is not in":
				let b = String(value).split(",");
				return (row) => !b.contains(String(row[column]));
			case "is NULL":
				return (row) =>
					String(row[column]) === "" ||
					(row[column] || null) === null;
			case "is not NULL":
				return (row) =>
					!(
						String(row[column]) === "" ||
						(row[column] || null) === null
					);
			default:
				return (row) => true;
		}
	};

	const buildFilterFns = () => {
		const filterFns = [];
		for (let filter of filters) {
			filterFns.push(buildFilterFn(filter));
		}
		return filterFns;
	};

	const defaultOperation = (column) => {
		const type =
			dataType === "cal"
				? CAL_COLUMN_TYPES[column]
				: COLUMN_TYPES[column];
		if (type === "text") {
			return "is";
		} else {
			return "=";
		}
	};

	const updateFilter = (index, filter) => {
		const newFilters = [...filters];
		newFilters[index] = filter;
		setFilters(newFilters);
	};

	const deleteFilter = (index) => {
		const newFilters = [...filters];
		newFilters.splice(index, 1);
		setFilters(newFilters);
	};

	const createNewFilter = (column) => {
		const newFilter = {
			column: column,
			operation: defaultOperation(column),
			value: null,
		};
		const newFilters = [...filters, newFilter];
		setFilters(newFilters);
	};

	const mapFilters = () => {
		return [
			...filters.map((filter, index) => (
				<ValidationFilterRow
					filter={filter}
					index={index}
					updateFilter={updateFilter}
					deleteFilter={deleteFilter}
					dataType={dataType}
				/>
			)),
			<ValidationNewFilterRow
				createNewFilter={createNewFilter}
				dataType={dataType}
			/>,
		];
	};

	const getFilterString = () => {
		return filters
			.map(
				({ column, operation, value }) =>
					`${(dataType === "cal" ? CAL_AQMET_COLUMNS : AQMET_COLUMNS)[column]} ${operation} ${!["is NULL", "is not NULL"].includes(operation) ? value : ""}`,
			)
			.join(", ");
	};

	const clearFilters = () => {
		setFilters([]);
		applyFilters([]);
		setFilterString("");
	};

	const canSubmit = () => {
		return !filters.some(
			(filter) =>
				filter.value === null &&
				filter.operation !== "is NULL" &&
				filter.operation !== "is not NULL",
		);
	};

	const handleSubmit = () => {
		applyFilters(buildFilterFns(filters));
		setFilterString(getFilterString());
	};

	return (
		<Modal id="validation-filter-modal" isOpen={isOpen} toggle={toggle}>
			<ModalHeader toggle={toggle}>Filters</ModalHeader>
			<ModalBody id="validation-filter-modal-body">
				<table id="validation-filter-table">
					<tbody>{mapFilters()}</tbody>
				</table>
			</ModalBody>
			<ModalFooter>
				<Button
					id="validation-filter-modal-clear-button"
					onClick={clearFilters}
					disabled={!filters.length}
				>
					Clear Filters
				</Button>
				<Button
					className="submit-button"
					onClick={handleSubmit}
					disabled={!canSubmit()}
				>
					Apply Filters
				</Button>
			</ModalFooter>
		</Modal>
	);
}

function ValidationFilterRow({
	filter,
	index,
	updateFilter,
	deleteFilter,
	dataType,
}) {
	const mapColumns = () => {
		return Object.entries(
			dataType === "cal" ? CAL_AQMET_COLUMNS : AQMET_COLUMNS,
		).map(([key, value]) => <option value={key}>{value}</option>);
	};

	const mapOperations = () => {
		if (
			(dataType === "cal"
				? CAL_COLUMN_TYPES[filter.column]
				: COLUMN_TYPES[filter.column]) === "text"
		) {
			return [
				"is",
				"is not",
				"is in",
				"is not in",
				"is NULL",
				"is not NULL",
			].map((operation) => <option>{operation}</option>);
		} else {
			return ["=", ">", "<", ">=", "<=", "is NULL", "is not NULL"].map(
				(operation) => <option>{operation}</option>,
			);
		}
	};

	const defaultOperation = (column) => {
		const type =
			dataType === "cal"
				? CAL_COLUMN_TYPES[column]
				: COLUMN_TYPES[column];
		if (type === "text") {
			return "is";
		} else {
			return "=";
		}
	};

	const handleColumn = (event) => {
		const value = event.target.value;
		const newFilter = {
			column: value,
			operation: defaultOperation(value),
			value: null,
		};
		updateFilter(index, newFilter);
	};

	const handleOperation = (event) => {
		const value = event.target.value;
		const newFilter = { ...filter, operation: value };
		updateFilter(index, newFilter);
	};

	const handleValue = (event) => {
		const value = event.target.value;
		const newFilter = { ...filter, value: value };
		updateFilter(index, newFilter);
	};

	const deleteRow = () => {
		deleteFilter(index);
	};

	return (
		<tr className="validation-filter-row">
			<td style={{ width: "150px" }}>
				<select
					className="validation-filter-column-select"
					value={filter.column}
					onChange={handleColumn}
				>
					{mapColumns()}
				</select>
			</td>
			<td style={{ width: "120px" }}>
				<select
					className="validation-filter-operation-select"
					value={filter.operation}
					onChange={handleOperation}
				>
					{mapOperations()}
				</select>
			</td>
			<td style={{ width: "180px", maxWidth: "180px" }}>
				<input
					className="validation-filter-value-input"
					type={
						dataType === "cal"
							? CAL_COLUMN_TYPES[filter.column]
							: COLUMN_TYPES[filter.column]
					}
					value={filter.value}
					onChange={handleValue}
					hidden={["is NULL", "is not NULL"].includes(
						filter.operation,
					)}
				/>
			</td>
			<td style={{ width: "28px" }}>
				<Button
					className="validation-filter-delete-button"
					onClick={deleteRow}
				>
					{utilIcons.trash(colors["ars-neutral-900"])}
				</Button>
			</td>
		</tr>
	);
}

function ValidationNewFilterRow({ createNewFilter, dataType }) {
	const mapColumns = () => {
		return [
			<option>Select Column...</option>,
			...Object.entries(
				dataType === "cal" ? CAL_AQMET_COLUMNS : AQMET_COLUMNS,
			).map(([key, value]) => <option value={key}>{value}</option>),
		];
	};

	const handleNewSelect = (event) => {
		event.preventDefault();
		event.stopPropagation();

		const value = event.target.value;
		event.target.value = "Add new...";
		createNewFilter(value);
	};

	return (
		<tr className="validation-new-filter-row">
			<td style={{ width: "150px" }}>
				<select value="Select Column..." onChange={handleNewSelect}>
					{mapColumns()}
				</select>
			</td>
			<td style={{ width: "80px" }} />
			<td style={{ width: "180px", maxWidth: "180px" }} />
			<td style={{ width: "28px" }} />
		</tr>
	);
}

function ValidationInsertModal({ isOpen, toggle, onSubmit, selectedSite }) {
	const [rows, setRows] = useState([]);
	const [parCodeList, setParCodeList] = useState([]);
	const [valCodeList, setValCodeList] = useState([]);
	const [aqsAgencyList, setAqsAgencyList] = useState([]);

	const { dataType } = useContext(DataValidationContext);
	const { sendGetRequest, sendPostRequest } = useContext(RestContext);

	useEffect(() => {
		const payload = {
			siteId: selectedSite,
			calTypes: null,
			minDate: moment().subtract(5, "years").toISOString(),
			maxDate: moment().toISOString(),
		};
		sendPostRequest(
			VALIDATION_URL + "/parameters/cal",
			payload,
			(response) => {
				setParCodeList(response.data);
			},
		);

		sendGetRequest(
			VALIDATION_URL +
				"/valCodes/" +
				(dataType === "cal" ? "cal/" : "") +
				selectedSite,
			(response) => {
				const responseData = response.data;
				responseData.sort((a, b) =>
					a.validationCode > b.validationCode ? 1 : -1,
				);
				setValCodeList(response.data);
			},
		);

		sendGetRequest(VALIDATION_URL + "/aqs/" + selectedSite, (response) => {
			const responseData = response.data;
			responseData.sort((a, b) => (a.aqsCode > b.aqsCode ? 1 : -1));
			setAqsAgencyList(response.data);
		});
	}, [selectedSite]);

	useEffect(() => {
		const newRow =
			dataType === "cal"
				? {
						siteNo: null,
						recordDate: moment()
							.minute(0)
							.format("YYYY-MM-DDTHH:mm"),
						parCode: null,
						calType: "Zero",
						valType: "ACTUAL",
						calVal: null,
						flags: null,
						validationCode: null,
						valCalVal: null,
						controlVal: null,
						aqsAgency: null,
						comment: null,
					}
				: {
						siteNo: null,
						recordDate: moment()
							.minute(0)
							.format("YYYY-MM-DDTHH:mm"),
						parCode: null,
						validationCode: null,
						controlVal: null,
						validatedVal: null,
						rawVal: null,
						sourceCode: "E",
						loggerFlag: null,
						screenFlag: null,
						status: "U",
						updateDate: null,
						updateBy: null,
						insertDate: null,
						qualifierCode: null,
					};
		setRows([newRow]);
	}, [isOpen]);

	const updateRow = (event, index) => {
		const { name, value } = event.target;
		const newRows = [...rows];
		rows[index][name] = value;
		setRows(newRows);
	};

	const deleteRow = (index) => {
		const newRows = [...rows];
		newRows.splice(index, 1);
		setRows(newRows);
	};

	const getCellContent = (types, col, row, index) => {
		if (col === "parCode") {
			return (
				<select
					name={col}
					value={row[col]}
					onChange={(e) => updateRow(e, index)}
				>
					<option value={null}></option>
					{parCodeList.map((parCode) => (
						<option>{parCode.parCode}</option>
					))}
				</select>
			);
		} else if (col === "calType") {
			return (
				<select
					name={col}
					value={row[col]}
					onChange={(e) => updateRow(e, index)}
				>
					<option>Span</option>
					<option>Prec</option>
					<option>Zero</option>
					<option>GSpan</option>
					<option>GZero</option>
					<option>Diag-Prec</option>
					<option>Mflow-Prec</option>
					<option>Tflow-Prec</option>
				</select>
			);
		} else if (col === "valType") {
			return (
				<select
					name={col}
					value={row[col]}
					onChange={(e) => updateRow(e, index)}
				>
					<option>ACTUAL</option>
					<option>INDICATED</option>
				</select>
			);
		} else if (col === "validationCode") {
			return (
				<select
					name={col}
					value={row[col]}
					onChange={(e) => updateRow(e, index)}
				>
					<option value={null}></option>
					{valCodeList.map((valCode) => (
						<option
							value={valCode.validationCode}
						>{`${valCode.validationCode} - ${valCode.reason}`}</option>
					))}
				</select>
			);
		} else if (col === "aqsAgency") {
			return (
				<select
					name={col}
					value={row[col]}
					onChange={(e) => updateRow(e, index)}
				>
					<option value={null}></option>
					{aqsAgencyList.map((agency) => (
						<option
							value={agency.aqsCode}
						>{`${agency.aqsCode} - ${agency.description}`}</option>
					))}
				</select>
			);
		} else if (col === "sourceCode" || col === "status") {
			return row[col];
		} else {
			return (
				<input
					name={col}
					type={types[col]}
					value={row[col]}
					onChange={(e) => updateRow(e, index)}
				/>
			);
		}
	};

	const mapColumnsToInput = (row, index) => {
		const excluded = ["siteNo", "updateDate", "updateBy", "insertDate"];
		const types = dataType === "cal" ? CAL_COLUMN_TYPES : COLUMN_TYPES;
		console.log(row);
		return Object.keys(types)
			.filter((col) => !excluded.includes(col))
			.map((col) => <td>{getCellContent(types, col, row, index)}</td>);
	};

	const mapRows = () => {
		return rows.map((row, index) => (
			<tr className="validation-insert-row">
				<td>
					<Button onClick={() => deleteRow(index)}>-</Button>
				</td>
				{mapColumnsToInput(row, index)}
			</tr>
		));
	};

	const mapHeaders = () => {
		const excluded = ["siteNo", "updateDate", "updateBy", "insertDate"];
		const texts = dataType === "cal" ? CAL_AQMET_COLUMNS : AQMET_COLUMNS;
		return Object.keys(texts)
			.filter((col) => !excluded.includes(col))
			.map((col) => <th>{texts[col]}</th>);
	};

	const addNewRow = () => {
		const newRows = [...rows];
		console.log(
			moment().minute(0).utcOffset(0, true).format("YYYY-MM-DDTHH:mmTZ"),
		);
		if (newRows.length) {
			const newRow = { ...newRows[newRows.length - 1] };
			newRows.push(newRow);
		} else {
			const newRow =
				dataType === "cal"
					? {
							siteNo: null,
							recordDate: moment()
								.minute(0)
								.utcOffset(0, true)
								.format("YYYY-MM-DDTHH:mm"),
							parCode: null,
							calType: "Zero",
							valType: "ACTUAL",
							calVal: null,
							flags: null,
							validationCode: null,
							valCalVal: null,
							controlVal: null,
							aqsAgency: null,
							comment: null,
						}
					: {
							siteNo: null,
							recordDate: moment()
								.minute(0)
								.format("YYYY-MM-DDTHH:mm"),
							parCode: null,
							validationCode: null,
							controlVal: null,
							validatedVal: null,
							rawVal: null,
							sourceCode: "E",
							loggerFlag: null,
							screenFlag: null,
							status: "U",
							updateDate: null,
							updateBy: null,
							insertDate: null,
							qualifierCode: null,
						};
			console.log(newRow.recordDate);
			newRows.push(newRow);
		}
		setRows(newRows);
	};

	const handleSubmit = () => {
		onSubmit(rows);
	};

	return (
		<Modal id="validation-insert-modal" isOpen={isOpen} toggle={toggle}>
			<ModalHeader toggle={toggle}>Insert Rows</ModalHeader>
			<ModalBody>
				<div id="validation-insert-table-wrapper">
					<table id="validation-insert-table">
						<thead>
							<tr>
								<th />
								{mapHeaders()}
							</tr>
						</thead>
						<tbody>
							{mapRows()}
							<tr id="validation-insert-new-row">
								<td />
								<td>
									<Button onClick={addNewRow}>New +</Button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<Button
					className="submit-button"
					disabled={!rows.length}
					onClick={handleSubmit}
				>
					Save
				</Button>
			</ModalBody>
		</Modal>
	);
}

function PlotZoomControls({ zoomLevel, setZoomLevel }) {
	const zoomIn = () => {
		if (zoomLevel >= 200) return;
		setZoomLevel(zoomLevel + 10);
	};

	const zoomOut = () => {
		if (zoomLevel <= 50) return;
		setZoomLevel(zoomLevel - 10);
	};

	return (
		<div id="plot-zoom-controls">
			<span>Zoom:</span>
			<Button
				className="plot-zoom-control-button"
				disabled={zoomLevel <= 50}
				onClick={zoomOut}
			>
				-
			</Button>
			<span
				style={{ width: "4ch", textAlign: "center" }}
			>{`${zoomLevel}%`}</span>
			<Button
				className="plot-zoom-control-button"
				disabled={zoomLevel >= 200}
				onClick={zoomIn}
			>
				+
			</Button>
		</div>
	);
}

function BlankFillControl({ blankFill, selectedSite }) {
	const [valCodeList, setValCodeList] = useState([]);
	const [validationCode, setValidationCode] = useState("PF");

	const { sendGetRequest } = useContext(RestContext);

	useEffect(() => {
		sendGetRequest(
			VALIDATION_URL + "/valCodes/" + selectedSite,
			(response) => {
				const responseData = response.data;
				responseData.sort((a, b) =>
					a.validationCode > b.validationCode ? 1 : -1,
				);
				setValCodeList(response.data);
			},
		);
	}, [selectedSite]);

	const mapValCodesToList = () => {
		if (!valCodeList) return null;
		return valCodeList.map((valCode) => (
			<option
				value={valCode.validationCode}
			>{`${valCode.validationCode} - ${valCode.reason}`}</option>
		));
	};

	const handleValCode = (event) => {
		setValidationCode(event.target.value);
	};

	return (
		<div id="blank-fill-control">
			<select value={validationCode} onChange={handleValCode}>
				{mapValCodesToList()}
			</select>
			<Button onClick={() => blankFill(validationCode)}>
				{utilIcons.caret(colors["ars-neutral-900"], {
					rotate: "-90deg",
				})}
			</Button>
		</div>
	);
}

export default DataValidation;
